/** @format */

import { CartTelemetry, Checkout } from '@cart-checkout/data-access/interfaces';

import { AnalyticsService } from '../analytics.service';
import { Injectable } from '@angular/core';
import {
  CartAction,
  CartItem,
  ICart,
  StandaloneWarrantyAddedToCart,
} from '@shared/data-access/interfaces';

/** @dynamic */
@Injectable({
  providedIn: 'root',
})
export class CartAnalyticsService extends AnalyticsService {
  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setCartId
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param cartId -- orderId from cart
   *
   * @description
   * Sets the cartId reveived orderId in response from addtocart.
   */
  public setCartId(cartId: any): void {
    this.window.siteData.CartID = cartId;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setNewCart
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param isNewCart - Whether or not the Cart action resulted in a new Cart
   *
   * @description
   * Sets the boolean for whether the Cart action resulted in a new Cart
   */
  public setNewCart(isNewCart: boolean): void {
    this.window.siteData.NewCart = `${isNewCart}`;
  }

  /**
   * @description
   * Sets the Items Added to the Cart on the Object.
   *
   * @param inItemsAddedToCart - Array of Objects added to the Cart
   */
  public setItemsAddedToCart(inItemsAddedToCart: any[]): void {
    this.window.siteData.ProductsAddedToCart = inItemsAddedToCart;
  }

  public setWarrantiesAddedToCart(warrantiesAddedToCart: StandaloneWarrantyAddedToCart[]): void {
    this.window.siteData.WarrantiesAddedToCart = warrantiesAddedToCart;
  }

  public triggerCartSanitizationEvent(cart: ICart): void {
    if (!cart || !cart.actions || !cart.actions.length) return;

    cart.actions.forEach((action: CartAction) => {
      // Prepare the event body
      const eventBody = {
        ProductID: action.product?.ecode,
        ProductSKU: action.product?.sku,
        Action: action.action,
        ActionReason: action.reason,
        Message: action.message?.replace(`${action.product?.sku} `, '').trim().toLowerCase(),
        ProductOOS: action.message?.includes('out of stock'),
      };
      // Trigger the analytics event
      this.triggerAnalyticsEvent('CartSanitizationAction', eventBody);
    });
  }

  /**
   * @description
   * Sets the Items Added to the Cart on the Object.
   *
   * @param inItemsAddedToCart - Array of Objects added to the Cart
   */
  public setBasePropsForCart(cart: ICart, cartEditEnabled = false): void {
    if (this.window.siteData.ProductArray) {
      for (const [index, item] of cart.contents.entries()) {
        // Check for Sku
        let productArrayItem = this.window.siteData.ProductArray.find(
          (product) => product.ProductSKU === item.sku_base,
        );
        // If no sku, use index
        if (!productArrayItem) {
          productArrayItem = this.window.siteData.ProductArray[index];
        }
        if (item?.procure?.mode) {
          productArrayItem.FulfillmentMethod = item.procure.mode;
        }
        productArrayItem['Editable'] = !!(
          cartEditEnabled &&
          item.qty === 1 &&
          !item.my_customizer_doc &&
          !item.personalized
        );
        if (item.procure.store) {
          productArrayItem.FulfillmentStore = item.procure.store;
        }
      }
    }
    this.window.siteData.Quantity = cart.count;
  }

  public setWarrantyPropsForCart(cart: ICart, checkout: Checkout): void {
    if (!this.window.siteData.ProductArray) return;

    const checkoutItemMap = new Map(
      checkout?.cart?.items?.filter(Boolean).map((item) => [item?.sku, item]),
    );

    cart.contents.forEach((item) => {
      const productArrayItem = this.findProductArrayItem(item);
      if (productArrayItem) {
        const checkoutItem = item.sku ? checkoutItemMap.get(item.sku) : undefined;
        this.setWarrantyEligibility(productArrayItem, item);
        this.setWarrantyValues(productArrayItem, checkoutItem);
      }
    });
  }

  private findProductArrayItem(item: any): any {
    return this.window.siteData.ProductArray.find(
      (product) => product.ProductSKU === item.sku_base,
    );
  }

  private setWarrantyEligibility(productArrayItem: any, item: any): void {
    if (item.warranty_eligible !== null && item.warranty_eligible !== undefined) {
      productArrayItem.WarrantyEligible = item.warranty_eligible;
    }
  }

  private setWarrantyValues(productArrayItem: any, checkoutItem: CartItem | undefined): void {
    if (!checkoutItem?.availableWarranties?.length || !checkoutItem?.addons?.length) return;

    const warrantyAddons = checkoutItem.addons.filter((addon) => addon.type === 'warranty');

    let warrantiesQtyTotal = 0;
    let warrantiesPriceTotal = 0;
    const warrantyEcodes: string[] = [];

    for (const addon of warrantyAddons) {
      warrantiesQtyTotal += addon.qty;

      const matchingWarranty = checkoutItem.availableWarranties.find(
        (warranty) => warranty.planSku === addon.sku,
      );

      if (matchingWarranty) {
        warrantiesPriceTotal += matchingWarranty.sellingPrice * addon.qty;
        warrantyEcodes.push(matchingWarranty.planEcode);
      }
    }

    productArrayItem.WarrantyEcode = warrantyEcodes.join('|');
    productArrayItem.WarrantyQuantity = warrantiesQtyTotal;
    productArrayItem.WarrantyAveragePrice = warrantiesQtyTotal
      ? warrantiesPriceTotal / warrantiesQtyTotal
      : 0;
  }

  /**
   * @description
   * set siteData object for empty cart
   */
  public setEmptyCartProperites() {
    this.window.siteData.CartID = '';
    this.window.siteData.OrderID = '';
    this.window.siteData.PaypalAvailable = false;
    // Totals
    this.window.siteData.MerchandiseSubtotal = '0';
    this.window.siteData.ShippingTotal = '0';
    this.window.siteData.TaxTotal = '0';
    this.window.siteData.TotalOrderDiscount = '0';
    this.window.siteData.OrderTotal = '0';
    this.window.siteData.ShippingDiscountAmount = '0';
    this.window.siteData.ProductArray = [];
    this.window.siteData.OrderLevelPromoCodes = [''];
    this.window.siteData.OrderLevelPromoAmounts = [];
    this.window.siteData.Quantity = '0';
  }

  /**
   * @description
   * Trigger a cart ghost drop event
   *  *
   * @param payload - CartTelemetry interface object
   */
  public triggerCartGhostDropEvent(payload: CartTelemetry): void {
    const { event, details } = payload;
    this.triggerCartActionEvent(event, details);
  }

  /**
   * @description
   * Triggers a cart action event and dispatches a custom event with the siteData Object
   *
   * @param actionType - The type of action being performed
   * @param eventData - Optional additional data to merge with the siteData
   */
  public triggerCartActionEvent(actionType: string, eventData?: object): void {
    this.window.siteData.EventType = 'CartAction';
    this.window.siteData.ActionType = actionType;

    const additionalDetails = {
      ...this.getAdditionalDetailsWithDsgUID(),
      ...eventData,
    };

    this.logger.log(
      'AnalyticsService.triggerCartActionEvent -- Event CartAction triggered: ',
      additionalDetails,
    );

    // Trigger the Event and pass along the siteData Object
    document.dispatchEvent(this.createCustomEvent('CartAction', additionalDetails));
  }
}
